import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import type { ConceptId } from "@storytell/types";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbApps, TbX } from "solid-icons/tb";
import { onCleanup, onMount } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useWire } from "~/wire";

export const VariableV1Concept = (props: {
  notInPromptBar?: boolean;
  id: string;
  selected?: boolean;
  onDelete: () => void;
  isEditable?: boolean;
  text?: string;
}) => {
  const wire = useWire();
  const concept = () => wire.concepts.get(props.id as ConceptId);

  // TODO: @andi, Better way to communicate between prompt bar and app state?
  onMount(() => {
    if (props.notInPromptBar) return;
    const unsubscribe = subscribeToGlobalEvent("removed-item-from-prompt-scope", (event) => {
      if (event.detail.id === props.id) {
        props.onDelete();
      }
    });
    dispatchGlobalEvent("added-mention-to-prompt-bar", {
      id: props.id,
    });
    onCleanup(() => {
      unsubscribe();
      dispatchGlobalEvent("removed-mention-from-prompt-bar", {
        id: props.id,
      });
    });
  });

  const truncatedName = () => {
    const maxLength = 40;
    const name = concept()?.name || props.text || "Unknown";
    if (name.length <= maxLength) return name;

    const extIndex = name.lastIndexOf(".");
    const extension = extIndex !== -1 ? name.slice(extIndex) : "";
    const nameWithoutExt = extIndex !== -1 ? name.slice(0, extIndex) : name;

    const charsToShow = maxLength - extension.length - 3; // 3 for "..."
    if (charsToShow <= 0) return name;

    const frontChars = Math.ceil(charsToShow / 2);
    const backChars = Math.floor(charsToShow / 2);

    return `${nameWithoutExt.slice(0, frontChars)}...${nameWithoutExt.slice(nameWithoutExt.length - backChars)}${extension}`;
  };

  const conceptName = concept()?.name || props.text || "Unknown Concept";

  return (
    <span
      onClick={(e) => {
        // Don't navigate if clicking the delete button
        if ((e.target as HTMLElement).closest("button")) return;
        e.preventDefault();
        wire.knowledgeUi.open();
        wire.knowledgeUi.setActiveTab("concepts");
        dispatchGlobalEvent("open-concept-in-pane", { conceptId: props.id });
      }}
      class={twMerge(
        "group/mention inline-flex items-center gap-1 bg-concept-bg rounded-lg text-sm text-concept-text hover:bg-concept-bg-hover border px-1 border-concept-border cursor-pointer",
        props.selected && "ring-2 ring-concept-border",
      )}
    >
      <TbApps class="size-3.5 group-hover/mention:hidden" />
      <Show when={props.isEditable}>
        <StTooltip placement="top" contentProps={{ class: "z-20" }} content={<p>Remove</p>}>
          <StTooltipTrigger
            as="button"
            onClick={() => {
              props.onDelete();
            }}
            type="button"
            class="transition-transform text-concept-icon cursor-pointer hidden group-hover/mention:flex items-center gap-1"
          >
            <TbX class="size-3.5 text-secondary-text hover:[stroke-width:3] hover:drop-shadow-sm" />
            <span class="text-secondary-text pointer-events-none cursor-default"> | </span>
          </StTooltipTrigger>
        </StTooltip>
      </Show>
      <span class="hover:font-bold">{truncatedName()}</span>
    </span>
  );
};
